import React, { useCallback, useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { REVIEW_TYPE_OF_PROVIDER, REVIEW_TYPE_OF_CUSTOMER, propTypes } from '../../util/types';
import { ensureCurrentUser, ensureUser } from '../../util/data';
import { isScrollingDisabled } from '../../ducks/ui.duck';
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import {
  Page,
  AvatarLarge,
  NamedLink,
  Reviews,
  ButtonTabNavHorizontal,
  IconSuccess,
  EnglishFlag,
  FrenchFlag,
  GermanFlag,
  ItalianFlag,
  Logo,
  ListingCardWithoutLink,
  LoadMore,
  ProfileActivities,
  ExternalLink,
  LayoutSingleColumn,
} from '../../components';
import Flag from 'react-world-flags';
import { useSelector } from 'react-redux';
import ReviewsModal from './ReviewsModal';
import { queryUserListings, queryUserReviews } from './ProfilePage.duck';
import { dateFormat } from '../../util/dates';
import { instagramPageURL } from '../../util/urlHelpers';

import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import NotFoundPage from '../NotFoundPage/NotFoundPage';
import { capitalizeWord } from '../../util/richText';
import { useConfiguration } from '../../context/configurationContext';

import css from './ProfilePage.module.css';
import { getLocaleFromUrl } from '../../constants/locales';
import { createURLParamsForListing } from '../../util/listingHelpers';
import ListingService from '../../services/ListingService';
import { LOCALE_TO_LANG } from '../../constants/locales';
import getCountryCodes from '../../translations/countryCodes';

// Helper to get localized listing title for SEO (same logic as SectionListings)
const getLocalizedListingTitle = (listing, language, intl) => {
  const publicData = listing?.attributes?.publicData;
  const discipline = publicData?.discipline;
  const cityName = publicData?.multiLangAddressParts?.[language]?.city;
  const authorName = listing?.author?.attributes?.profile?.displayName;
  
  const translatedDiscipline = discipline 
    ? ListingService.generalMessages[language]?.[`General.${discipline}`] || discipline
    : null;
  
  const inText = ListingService.generalMessages[language]?.['General.in'] || 'in';
  const withText = ListingService.generalMessages[language]?.['General.with'] || 'with';
  
  let title = '';
  if (translatedDiscipline) title += translatedDiscipline.toLowerCase();
  if (cityName) title += ` ${inText} ${cityName}`;
  if (authorName) title += ` ${withText} ${authorName}`;
  
  return title.trim() || listing?.attributes?.title || '';
};

const getLanguageFlag = language => {
  switch (language) {
    case 'english':
      return <EnglishFlag className={css.flagIcon} />;
    case 'french':
      return <FrenchFlag className={css.flagIcon} />;
    case 'german':
      return <GermanFlag className={css.flagIcon} />;
    case 'italian':
      return <ItalianFlag className={css.flagIcon} />;
    default:
      break;
  }

  return null;
};

const getCountryFlagEmoji = countryCode => {
  if (!countryCode || countryCode.length !== 2) return null;

  const codePoints = countryCode
    .toUpperCase()
    .split('')
    .map(char => 127397 + char.charCodeAt());
  return String.fromCodePoint(...codePoints);
};

const getDocumentTypeLabel = (type, intl) => {
  const types = {
    professional_certificate: intl.formatMessage({
      id: 'AddActivityForm.docTypeProfessionalCert',
      defaultMessage: 'Professional Certificate',
    }),
    association_registration: intl.formatMessage({
      id: 'AddActivityForm.docTypeAssociationReg',
      defaultMessage: 'Association Registration',
    }),
    safety_certificate: intl.formatMessage({
      id: 'AddActivityForm.docTypeSafetyCert',
      defaultMessage: 'Safety Certificate',
    }),
    insurance_coverage: intl.formatMessage({
      id: 'AddActivityForm.docTypeInsuranceCoverage',
      defaultMessage: 'Proof of Insurance Coverage',
    }),
    chamber_of_commerce: intl.formatMessage({
      id: 'AddActivityForm.docTypeChamberCommerce',
      defaultMessage: 'Chamber of Commerce',
    }),
    business_license: intl.formatMessage({
      id: 'AddActivityForm.docTypeBusinessLicense',
      defaultMessage: 'Business License',
    }),
  };

  return types[type] || intl.formatMessage({ id: 'General.document', defaultMessage: 'Document' });
};

const getIconSuccessVerifyType = isVerified => (isVerified ? 'verified' : 'notverified');

const panelMediumWidth = 50;
const panelLargeWidth = 62.5;
const cardRenderSizes = [
  '(max-width: 767px) 100vw',
  `(max-width: 1023px) ${panelMediumWidth}vw`,
  `(max-width: 1920px) ${panelLargeWidth / 2}vw`,
  `${panelLargeWidth / 3}vw`,
].join(', ');

const buildProfileSchema = (user, config, intl, locale) => {
  if (!user?.attributes?.profile) return null;

  const { profile } = user.attributes;
  const profileId = user?.id?.uuid;
  const { displayName, bio, publicData = {} } = profile;
  const {
    activities = {},
    languages = [],
    locations = [],
    language = 'en' // fallback to English if not provided
  } = publicData;

  // Extract disciplines from activities
  const disciplines = Object.entries(activities).flatMap(([_, items]) =>
    items.map(item => intl.formatMessage({ id: `General.${item?.discipline}` }))
  );

  // Format available languages
  const formattedLanguages = languages.map(lang => ({
    "@type": "Language",
    "name": lang.charAt(0).toUpperCase() + lang.slice(1).toLowerCase()
  }));

  const marketplaceName = config?.marketplaceName || "Crion";
  const marketplaceRootURL = config?.marketplaceRootURL || "https://crion.org";
  // locale passed explicitly from the calling component (interfaceLang from Redux, reliable on SSR)
  const resolvedLocale = locale || getLocaleFromUrl();
  const profileUrl = `${marketplaceRootURL}/${resolvedLocale}/u/${profileId}`;

  const schema = {
    "@context": "https://schema.org",
    "@type": "Person",
    "@id": profileUrl,
    "url": profileUrl,
    "name": displayName,
    "description": {
      "@value": bio || "",
      "@language": language
    },
    "affiliation": {
      "@type": "Organization",
      "name": marketplaceName,
      "url": marketplaceRootURL
    },
    "knowsAbout": [
      ...disciplines,
      ...locations
    ],
    "mainEntityOfPage": {
      "@type": "WebPage",
      "url": profileUrl
    }
  };

  if (formattedLanguages.length > 0) {
    schema.knowsLanguage = formattedLanguages;
  }

  // if (instagram) {
  //   schema.sameAs = [`https://instagram.com/${instagram}`];
  // }

  return schema;
};

const ProfilePageComponent = props => {
  const {
    scrollingDisabled,
    currentUser,
    user,
    userShowError,
    intl,
    listings,
    listingsPagination,
    listingsQueryParams,
    queryListingsInProgress,
    queryUserListings,
    reviews,
    queryReviewsError,
    reviewsQueryParams,
    queryReviewsInProgress,
    reviewsPagination,
    queryUserReviews,
    totalHostReviews,
    totalGuestsReviews,
  } = props;

  const config = useConfiguration();
  const interfaceLang = useSelector(state => state.ui.interfaceLang);

  const [showReviewsType, setShowReviewsType] = useState(REVIEW_TYPE_OF_PROVIDER);

  const ensuredCurrentUser = useMemo(() => ensureCurrentUser(currentUser), [currentUser]);
  const profileUser = useMemo(() => ensureUser(user), [user]);
  const isCurrentUser = useMemo(
    () =>
      ensuredCurrentUser.id && profileUser.id && ensuredCurrentUser.id.uuid === profileUser.id.uuid,
    [ensuredCurrentUser.id, profileUser.id]
  );

  const { profile, createdAt } = useMemo(() => profileUser.attributes, [profileUser.attributes]);
  const { publicData, displayName, bio, metadata } = useMemo(() => profile || {}, [profile]);
  const {
    languages,
    locations,
    activities = {},
    instagramFeed,
    instagram: instagramUrl,
    certificatesMetadata: publicCertificatesMetadata = [],
    verification,
  } = useMemo(
    () => publicData || {},
    [publicData]
  );

  const certificatesEvidence = useMemo(() => verification?.evidence || [], [verification]);

  // Determine if user is a provider based on public data
  const userIntent = useMemo(() => {
    // If user has verification data or certificates, they are likely a provider
    if (verification?.evidence?.length > 0 || verification?.access) {
      return 'provider';
    }
    return undefined;
  }, [verification]);

  const { isInstagramVerified, isEmailVerified, isStripeVerified, certificates: legacyCertificates = [] } = useMemo(
    () => metadata || {},
    [metadata]
  );

  // Merge legacy certificates into certificatesMetadata structure for display if needed
  // This ensures backward compatibility for profiles that haven't been migrated yet
  const certificatesMetadata = useMemo(() => {
    if (publicCertificatesMetadata.length > 0) {
      return publicCertificatesMetadata;
    }
    
    // Fallback: Convert legacy certificates to new format for display
    if (legacyCertificates.length > 0) {
      const converted = [];
      legacyCertificates.forEach(cert => {
        if (cert.files && Array.isArray(cert.files)) {
          cert.files.forEach(file => {
            converted.push({
              fileName: file.name,
              disciplines: [cert.discipline],
              isVerified: cert.isVerified,
              // Legacy data doesn't have these fields
              country: null,
              association: null
            });
          });
        }
      });
      return converted;
    }
    
    return [];
  }, [publicCertificatesMetadata, legacyCertificates]);

  const memberSinceYear = useMemo(
    () => (createdAt instanceof Date ? createdAt.getFullYear() : null),
    [createdAt]
  );

  const countryNameByCode = useMemo(() => {
    const codes = getCountryCodes(intl.locale || 'en-US');
    return codes.reduce((acc, c) => {
      acc[c.code] = c.name;
      return acc;
    }, {});
  }, [intl.locale]);

  const locationsByCountry = useMemo(() => {
    if (!Array.isArray(locations)) return {};
    const localeKey = interfaceLang || intl.locale || getLocaleFromUrl() || 'en';
    return locations.reduce((acc, location) => {
      let country = '';
      let display = '';

      if (typeof location === 'string') {
        const parts = location.split(',');
        country = parts.length > 1 ? parts[parts.length - 1].trim() : location;
        display = location;
      } else if (location && typeof location === 'object') {
        // structured location object
        // Prefer localized parts saved under the locale key, fall back to place/context
        const ml = location.multiLangAddressParts && location.multiLangAddressParts[localeKey];
        display = ml && ml.city ? `${ml.city}${ml.region ? ', ' + ml.region : ''}${ml.country ? ', ' + ml.country : ''}` : (location.address || '');

        country = (ml && ml.country) || (location.place && Array.isArray(location.place.context) && (() => {
          const match = location.place.context.find(c => c.id && c.id.split('.')[0] === 'country');
          return match ? match.text : undefined;
        })()) || '';
      }

      if (!country) country = display || 'Unknown';
      if (!acc[country]) acc[country] = [];
      acc[country].push(display);
      return acc;
    }, {});
  }, [locations]);

  // Responsive grid columns: compute max columns per breakpoint and
  // cap it by the number of items so cards fill the available space
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1200
  );

  useEffect(() => {
    const onResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const getMaxColumnsForWidth = width => {
    if (width < 600) return 1; // small
    if (width < 992) return 2; // medium
    return 3; // large
  };

  const certCount = Array.isArray(certificatesEvidence) ? certificatesEvidence.length : 0;
  const certColumns = Math.min(Math.max(certCount, 1), getMaxColumnsForWidth(windowWidth));

  const locationCount = Object.keys(locationsByCountry || {}).length;
  const locationColumns = Math.min(Math.max(locationCount, 1), getMaxColumnsForWidth(windowWidth));

  const activityCount = activities ? Object.keys(activities).length : 0;
  const activitiesColumns = Math.min(Math.max(activityCount, 1), getMaxColumnsForWidth(windowWidth));

  const totalReviews = useMemo(() => totalHostReviews + totalGuestsReviews, [
    totalHostReviews,
    totalGuestsReviews,
  ]);

  const messages = useMemo(
    () => ({
      email: intl.formatMessage({ id: 'General.email' }),
      instagram: intl.formatMessage({ id: 'General.instagram' }),
      identity: intl.formatMessage({ id: 'General.identity' }),
      reviews: intl.formatMessage({ id: 'General.reviews' }).toLowerCase(),
      language: intl.formatMessage({ id: 'General.language' }),
      locations: intl.formatMessage({ id: 'General.locations' }).toLowerCase(),
      listings: intl.formatMessage({ id: 'General.listings' }).toLowerCase(),
      bio: intl.formatMessage({ id: 'General.bio' }).toLowerCase(),
      schemaTitle: intl.formatMessage(
        {
          id: 'ProfilePage.schemaTitle',
        },
        {
          name: displayName,
          siteTitle: config.marketplaceName,
        }
      ),
    }),
    [intl, displayName, config]
  );

  const reviewsOfProvider = useMemo(
    () => reviews.filter(r => r.attributes.type === REVIEW_TYPE_OF_PROVIDER),
    [reviews]
  );

  const reviewsOfCustomer = useMemo(
    () => reviews.filter(r => r.attributes.type === REVIEW_TYPE_OF_CUSTOMER),
    [reviews]
  );

  const showOfProviderReviews = useCallback(() => {
    queryUserReviews({ ...reviewsQueryParams, type: 'ofCustomer' });
    setShowReviewsType(REVIEW_TYPE_OF_PROVIDER);
  }, [queryUserReviews, reviewsQueryParams]);

  const showOfCustomerReviews = useCallback(() => {
    queryUserReviews({ ...reviewsQueryParams, type: 'ofProvider' });
    setShowReviewsType(REVIEW_TYPE_OF_CUSTOMER);
  }, [queryUserReviews, reviewsQueryParams]);

  const reviewTabs = useMemo(
    () => [
      {
        text: (
          <h3 className={css.desktopReviewsTitle}>
            <FormattedMessage
              id="ProfilePage.reviewsOfProviderTitle"
              values={{ count: totalHostReviews }}
            />
          </h3>
        ),
        selected: showReviewsType === REVIEW_TYPE_OF_PROVIDER,
        onClick: showOfProviderReviews,
      },
      {
        text: (
          <h3 className={css.desktopReviewsTitle}>
            <FormattedMessage
              id="ProfilePage.reviewsOfCustomerTitle"
              values={{ count: totalGuestsReviews }}
            />
          </h3>
        ),
        selected: showReviewsType === REVIEW_TYPE_OF_CUSTOMER,
        onClick: showOfCustomerReviews,
      },
    ],
    [
      showOfCustomerReviews,
      showOfProviderReviews,
      showReviewsType,
      totalGuestsReviews,
      totalHostReviews,
    ]
  );

  const schema = buildProfileSchema(profileUser, config, intl, interfaceLang || intl.locale);

  if (userShowError && userShowError.status === 404) {
    return <NotFoundPage />;
  }

  return (
    <Page
      scrollingDisabled={scrollingDisabled}
      title={messages.schemaTitle}
      schema={schema}
    >
      <LayoutSingleColumn
        topbar={<TopbarContainer currentPage="ProfilePage" />}
        footer={<FooterContainer />}
        mainColumnClassName={css.container}
      >
        <div className={css.aside}>
          <div className={css.asideContent}>
            <div className={css.asideHeader}>
              <AvatarLarge className={css.avatar} user={user} disableProfileLink />
              <div className={css.userInfoCard}>

                <div className={css.userInfoContainer}>
                  <ul className={css.userInfoList}>
                    <li>
                      <IconSuccess
                        className={css.iconSuccess}
                        tagFill={getIconSuccessVerifyType(isEmailVerified)}
                      />
                      {messages.email}
                    </li>
                    {instagramUrl && (
                      <li>
                        <IconSuccess
                          className={css.iconSuccess}
                          tagFill={getIconSuccessVerifyType(isInstagramVerified)}
                        />
                        <ExternalLink href={instagramPageURL(instagramUrl)}>
                          {messages.instagram}
                        </ExternalLink>
                      </li>
                    )}
                    <li>
                      <IconSuccess
                        className={css.iconSuccess}
                        tagFill={getIconSuccessVerifyType(isStripeVerified)}
                      />
                      {messages.identity}
                    </li>
                  </ul>
                  <ul className={css.userInfoListRight}>
                    <li>
                      {totalReviews} {capitalizeWord(messages.reviews)}
                    </li>
                    <li>{messages.language}</li>
                    <li>
                      {languages?.map(lang => <span key={lang}>{getLanguageFlag(lang)}</span>)}
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <h1 className={css.desktopHeading}>
              <FormattedMessage
                id="ProfilePage.desktopHeading"
                values={{
                  name: <span className={css.emphasisedText}>{displayName}!</span>,
                }}
              />
              {isCurrentUser ? (
                <NamedLink className={css.editProfileLink} name="ProfileSettingsPage">
                  <FormattedMessage id="ProfilePage.editProfileLinkDesktop" />
                </NamedLink>
              ) : null}
            </h1>

            <span className={css.memberInfo}>
              <FormattedMessage
                id="ProfilePage.memberSince"
                values={{
                  year: memberSinceYear,
                }}
              />
            </span>
          </div>
        </div>
        {userShowError ? (
          <p className={css.error}>
            <FormattedMessage id="ProfilePage.loadingDataFailed" />
          </p>
        ) : (
          <div className={css.mainContent}>
            {Array.isArray(certificatesEvidence) && certificatesEvidence.length > 0 ? (
              <div className={css.profileSection}>
                <h3>
                  <FormattedMessage id="ProfilePage.certificatesEvidence" />
                </h3>
                <div
                  className={css.certificatesGrid}
                  style={{ gridTemplateColumns: `repeat(${certColumns}, 1fr)` }}
                >
                  {certificatesEvidence.map((cert, index) => {
                    const countryName = cert.country ? countryNameByCode[cert.country] || cert.country : null;
                    // Handle IDs: 1. No Extension (Backend), 2. Regular, 3. Encoded (Legacy)
                    const id = cert.id || '';
                    const lastDot = id.lastIndexOf('.');
                    const idNoExt = lastDot > 0 ? id.substring(0, lastDot) : id;
                    const encodedId = id ? id.replace(/\./g, '%2E') : null;
                    
                    const isVerified = verification?.documentStatusHistory?.[idNoExt]?.status === 'approved' ||
                                       verification?.documentStatusHistory?.[id]?.status === 'approved' ||
                                       (encodedId && verification?.documentStatusHistory?.[encodedId]?.status === 'approved');

                    return (
                      <div key={cert.id || index} className={css.certificateCard}>
                        <div className={css.certificateCardHeader}>
                          <div className={css.certificateHeaderLeft}>
                            <span className={css.certificateType}>
                              {getDocumentTypeLabel(cert.type, intl)}
                            </span>
                          </div>
                          <div className={css.certificateHeaderRight}>
                            {isVerified ? (
                              <span className={css.verifiedBadge}>
                                <IconSuccess className={css.iconSuccess} />
                                <FormattedMessage id="ProfilePage.verified" />
                              </span>
                            ) : null}
                            {cert.country ? (
                              <span className={css.certificateFlag}>
                                <Flag code={cert.country} alt={countryName || cert.country} />
                              </span>
                            ) : null}
                          </div>
                        </div>
                        <div className={css.certificateDetails}>
                          {countryName ? (
                            <div className={css.certificateDetail}>
                              <span className={css.certificateLabel}>
                                <FormattedMessage id="ProfilePage.country" />:
                              </span>
                              <span className={css.certificateValue}>
                                {countryName}
                              </span>
                            </div>
                          ) : null}
                          {cert.issuer ? (
                            <div className={css.certificateDetail}>
                              <span className={css.certificateLabel}>
                                <FormattedMessage id="ProfilePage.issuer" />:
                              </span>
                              <span className={css.certificateValue}>{cert.issuer}</span>
                            </div>
                          ) : null}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : null}
            {activityCount > 0 && (
              <ProfileActivities
                className={css.profileSection}
                activities={activities}
                documents={[]} // Don't show certificates section here - already shown above
                certificates={[]} // Pass empty array to disable legacy rendering in component
                certificatesMetadata={certificatesMetadata}
                intl={intl}
                activitiesGridColumns={activitiesColumns}
                currentUser={profileUser}
                userIntent={userIntent}
              />
            )}
            {Object.keys(locationsByCountry).length > 0 && (
              <div className={css.profileSection}>
                <h3>
                  <FormattedMessage id={`General.locations`} />
                </h3>
                <div
                  className={css.locationsGrid}
                  style={{ gridTemplateColumns: `repeat(${locationColumns}, 1fr)` }}
                >
                  {Object.entries(locationsByCountry).map(([country, countryLocations]) => (
                    <div key={country} className={css.locationCard}>
                      <div className={css.locationCardHeader}>{country}</div>
                      <div className={css.locationChipList}>
                        {countryLocations.map(location => (
                          <div key={location} className={css.locationChip}>
                            <span className={css.locationChipText}>{location}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {bio ? (
              <div className={css.profileSection}>
                <h3>
                  <FormattedMessage id={`General.bio`} />
                </h3>
                <p className={css.bio}>{bio}</p>
              </div>
            ) : null}
            {instagramFeed?.length > 0 && (
              <div className={css.profileSection}>
                <h3>
                  <FormattedMessage id={`General.instagramFeed`} />
                </h3>
                <div className={css.instagramFeed}>
                  {instagramFeed?.map(data => (
                    <ExternalLink key={data.id} href={data.link}>
                      <img title={data.caption} src={data.image} alt="" />
                      <span>{dateFormat(data.timestamp)}</span>
                    </ExternalLink>
                  ))}
                </div>
              </div>
            )}

            {listings && listings.length > 0 && (
              <div className={css.profileSection}>
                <h3>
                  <FormattedMessage id={`General.listings`} />
                </h3>
                <>
                  {/* Hidden navigation links for SEO crawlers - sr-only */}
                  <nav className="sr-only" style={{
                    position: 'absolute',
                    width: '1px',
                    height: '1px',
                    padding: 0,
                    margin: '-1px',
                    overflow: 'hidden',
                    clip: 'rect(0, 0, 0, 0)',
                    whiteSpace: 'nowrap',
                    border: 0,
                  }}>
                    <ul>
                      {listings.map(listing => {
                        const pd = listing?.attributes?.publicData || {};
                        const locale = interfaceLang || getLocaleFromUrl() || intl.locale;
                        const language = LOCALE_TO_LANG[locale] || 'english';
                        const localizedTitle = getLocalizedListingTitle(listing, language, intl);
                        const titleFromPublicData = pd.titles && pd.titles[language];
                        const title = titleFromPublicData || localizedTitle || listing?.attributes?.title || listing?.title || listing?.attributes?.name || listing?.id?.uuid;
                        const urlParams = createURLParamsForListing(listing, locale, intl);
                        const paramsWithLang = { ...urlParams, lang: locale };
                        return (
                          <li key={listing.id.uuid}>
                            <NamedLink name="ListingPage" params={paramsWithLang}>
                              {title}
                            </NamedLink>
                          </li>
                        );
                      })}
                    </ul>
                  </nav>
                  {listings.map(l => {
                    const locale = interfaceLang || getLocaleFromUrl() || intl.locale;
                    const urlParams = createURLParamsForListing(l, locale, intl);
                    const paramsWithLang = { ...urlParams, lang: locale };
                    return (
                      <NamedLink key={l.id.uuid} name="ListingPage" params={paramsWithLang}>
                        <ListingCardWithoutLink
                          className={css.listingCard}
                          listing={l}
                          currentUser={currentUser}
                          renderSizes={cardRenderSizes}
                          setActiveListing={() => { }}
                          config={config}
                        />
                      </NamedLink>
                    );
                  })}

                  <LoadMore
                    onClickLoadMore={params => queryUserListings(params)}
                    pagination={listingsPagination}
                    fetchParams={listingsQueryParams}
                    inProgress={queryListingsInProgress}
                  />
                </>
              </div>
            )}

            {totalReviews > 0 && (
              <div className={css.desktopReviews}>
                <h3>
                  <FormattedMessage id="General.reviews" />
                </h3>

                {totalReviews ? (
                <>
                  <ButtonTabNavHorizontal
                    tabRootClassName={css.tab}
                    className={css.desktopReviewsTabNav}
                    tabs={reviewTabs}
                  />

                  {queryReviewsError ? (
                    <p className={css.error}>
                      <FormattedMessage id="ProfilePage.loadingReviewsFailed" />
                    </p>
                  ) : null}

                  {showReviewsType === REVIEW_TYPE_OF_PROVIDER ? (
                    reviewsOfCustomer.length === 0 && queryReviewsInProgress ? (
                      <div>
                        <FormattedMessage
                          id="General.loadingItems"
                          values={{ items: messages.reviews }}
                        />
                      </div>
                    ) : reviewsOfCustomer.length ? (
                      <Reviews
                        reviews={reviewsOfCustomer.slice(0, 2)}
                        className={css.reviews}
                        reviewCardClassName={css.reviewCard}
                      />
                    ) : (
                      <span className={css.emptySection}>
                        <FormattedMessage
                          id="General.noItems"
                          values={{ items: messages.reviews }}
                        />
                      </span>
                    )
                  ) : reviewsOfProvider.length === 0 && queryReviewsInProgress ? (
                    <div>
                      <FormattedMessage
                        id="General.loadingItems"
                        values={{ items: messages.reviews }}
                      />
                    </div>
                  ) : reviewsOfProvider.length ? (
                    <Reviews
                      reviews={reviewsOfProvider.slice(0, 2)}
                      className={css.reviews}
                      reviewCardClassName={css.reviewCard}
                    />
                  ) : (
                    <span className={css.emptySection}>
                      <FormattedMessage id="General.noItems" values={{ items: messages.reviews }} />
                    </span>
                  )}
                </>
              ) : null}
            </div>
          )}
            <ReviewsModal
              reviews={reviews}
              fetchParams={reviewsQueryParams}
              pagination={reviewsPagination}
            />
          </div>
        )}
      </LayoutSingleColumn>
    </Page>
  );
};

ProfilePageComponent.defaultProps = {
  currentUser: null,
  user: null,
  userShowError: null,
  reviews: [],
  queryReviewsError: null,
};

const { bool, arrayOf } = PropTypes;

ProfilePageComponent.propTypes = {
  scrollingDisabled: bool.isRequired,
  currentUser: propTypes.currentUser,
  user: propTypes.user,
  userShowError: propTypes.error,
  reviews: arrayOf(propTypes.review),
  queryReviewsError: propTypes.error,

  // from injectIntl
  intl: intlShape.isRequired,
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  const { partner } = state.ui;
  const {
    userId,
    userShowError,
    reviews,
    queryReviewsError,
    listings,
    listingsPagination,
    listingsQueryParams,
    queryListingsInProgress,
    reviewsQueryParams,
    reviewsPagination,
    totalHostReviews,
    totalGuestsReviews,
    queryReviewsInProgress,
  } = state.ProfilePage;
  const userMatches = getMarketplaceEntities(state, [{ type: 'user', id: userId }]);

  const user = userMatches.length === 1 ? userMatches[0] : null;

  return {
    scrollingDisabled: isScrollingDisabled(state),
    currentUser,
    user,
    listings,
    reviewsQueryParams,
    reviewsPagination,
    userShowError,
    reviews,
    queryReviewsError,
    listingsPagination,
    listingsQueryParams,
    queryListingsInProgress,
    queryReviewsInProgress,
    totalHostReviews,
    totalGuestsReviews,
    partner,
  };
};

const mapDispatchToProps = dispatch => ({
  queryUserReviews: params => dispatch(queryUserReviews(params)),
  queryUserListings: params => dispatch(queryUserListings(params)),
});

const ProfilePage = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  ),
  injectIntl
)(ProfilePageComponent);

export default ProfilePage;
